'use client';

import type { FC } from 'react';
import { useCurrentTime } from '../../hooks/useCurrentTime';
import { cn } from '../../utils/cn';

const p2 = (n: number) => String(n).padStart(2, '0');

export const secondsUntil = (iso: string, now: number): number =>
  Math.max(0, Math.floor((new Date(iso).getTime() - now) / 1000));

export interface RainCountdownProps {
  startAt: string;
  endAt: string;
  className?: string;
}

export const RainCountdown: FC<RainCountdownProps> = ({ startAt, endAt, className }) => {
  const now = useCurrentTime();

  const secondsLeft = secondsUntil(endAt, now);
  const countdown = `${p2(Math.floor(secondsLeft / 60))}:${p2(secondsLeft % 60)}`;
  const totalSeconds = Math.max(1, (new Date(endAt).getTime() - new Date(startAt).getTime()) / 1000);
  const remainingWidth = `${Math.min(1, secondsLeft / totalSeconds) * 100}%`;
  const isUrgent = secondsLeft > 0 && secondsLeft < 60;

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span
        className={cn(
          'text-[28px] font-bold leading-none tabular-nums tracking-tight',
          isUrgent ? 'text-accent-red' : 'text-white',
        )}
      >
        {countdown}
      </span>

      <div
        aria-hidden
        className={cn('relative flex-1 overflow-hidden rounded-full bg-white/15', isUrgent ? 'h-2' : 'h-1.5')}
      >
        <div
          className={cn(
            'absolute inset-y-0 start-0 rounded-full transition-[width] duration-1000 ease-linear motion-reduce:transition-none',
            isUrgent ? 'bg-accent-red' : 'bg-white',
          )}
          style={{ width: remainingWidth }}
        />
      </div>
    </div>
  );
};
